import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { OcrConsentModal } from './OcrConsentModal';

export type OcrDocType = 'NFE' | 'RECIBO' | 'BOLETO' | 'EXTRATO';
export type OcrKind = 'pagamento' | 'documento_fiscal';

interface OcrCaptureFlowProps {
  workspaceId: string;
  acordoId: string;
  competencia: string; // YYYY-MM
  kind: OcrKind;
  docType: OcrDocType;
  onComplete?: (result: OcrLancamentoResult) => void;
}

interface SignResponse {
  key: string;
  url: string;
  expiresIn: number;
}

interface OcrLancamentoResult {
  record: { id: string; [key: string]: unknown };
  ocrResult: {
    docType: string;
    confianca: number;
    emitenteNome?: string;
    valorTotal?: number;
  };
  needsReview: boolean;
}

type Stage =
  | { type: 'idle' }
  | { type: 'consent_open' }
  | { type: 'awaiting_file' }
  | { type: 'uploading'; filename: string }
  | { type: 'extracting'; filename: string }
  | { type: 'done'; result: OcrLancamentoResult }
  | { type: 'error'; message: string };

const FOLDER_BY_DOC_TYPE: Record<OcrDocType, 'notas-fiscais' | 'comprovantes' | 'documentos'> = {
  NFE: 'notas-fiscais',
  RECIBO: 'comprovantes',
  BOLETO: 'comprovantes',
  EXTRATO: 'documentos',
};

const ALLOWED_MIME = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB — alinhado ao backend OCR

export function OcrCaptureFlow({
  workspaceId,
  acordoId,
  competencia,
  kind,
  docType,
  onComplete,
}: OcrCaptureFlowProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ type: 'idle' });

  function openConsent() {
    setStage({ type: 'consent_open' });
  }

  function handleConsent() {
    setStage({ type: 'awaiting_file' });
    // Abre file picker / câmera nativo na próxima tick
    setTimeout(() => inputRef.current?.click(), 50);
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // permite reselecionar mesmo arquivo
    if (!file) {
      setStage({ type: 'idle' });
      return;
    }
    if (file.size > MAX_BYTES) {
      setStage({ type: 'error', message: 'Arquivo maior que 8 MB. Reduza a resolução e tente novamente.' });
      return;
    }

    try {
      setStage({ type: 'uploading', filename: file.name });

      // 1) Reservar URL presigned no R2
      const signRes = await api.post<SignResponse>(
        `/v1/workspaces/${workspaceId}/uploads/sign`,
        {
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          folder: FOLDER_BY_DOC_TYPE[docType],
        },
      );

      // 2) PUT direto no R2 (sem passar pela API)
      const putRes = await fetch(signRes.url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!putRes.ok) {
        throw new Error(`Falha ao enviar arquivo (HTTP ${putRes.status})`);
      }

      setStage({ type: 'extracting', filename: file.name });

      // 3) Criar lançamento com OCR (síncrono — ~5-10s)
      const result = await api.post<OcrLancamentoResult>(
        `/v1/workspaces/${workspaceId}/ocr/lancamento`,
        {
          r2Key: signRes.key,
          docType,
          acordoId,
          competencia,
          kind,
        },
      );

      setStage({ type: 'done', result });

      const conf = Math.round(result.ocrResult.confianca * 100);
      if (result.needsReview) {
        toast.warning(
          `Documento criado em REVISÃO (confiança ${conf}%). Abra a fila de revisão para validar.`,
        );
      } else {
        toast.success(`Documento criado e aprovado automaticamente (confiança ${conf}%).`);
      }

      onComplete?.(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha desconhecida';
      setStage({ type: 'error', message: msg });
      toast.error(`Falha no envio: ${msg}`);
    }
  }

  function reset() {
    setStage({ type: 'idle' });
  }

  return (
    <div className="space-y-4">
      <OcrConsentModal
        open={stage.type === 'consent_open'}
        onOpenChange={(open) => {
          if (!open && stage.type === 'consent_open') setStage({ type: 'idle' });
        }}
        onConsent={handleConsent}
      />

      {/* Input escondido — disparado após consentimento */}
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MIME}
        capture="environment"
        className="hidden"
        onChange={handleFile}
        aria-hidden
      />

      {stage.type === 'idle' && (
        <Button
          type="button"
          variant="primary"
          onClick={openConsent}
          className="w-full sm:w-auto"
        >
          <Camera className="h-4 w-4" strokeWidth={1.8} />
          Capturar documento
        </Button>
      )}

      {(stage.type === 'awaiting_file') && (
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
          Aguardando seleção do arquivo…
        </div>
      )}

      {(stage.type === 'uploading' || stage.type === 'extracting') && (
        <div className="rounded-3 border border-card-stroke bg-card p-4 space-y-2">
          <div className="flex items-center gap-2.5 text-sm text-fg">
            <Loader2 className="h-4 w-4 animate-spin text-accent" strokeWidth={1.8} />
            {stage.type === 'uploading'
              ? `Enviando ${stage.filename} para o armazenamento…`
              : `Extraindo dados do documento (Claude Vision)…`}
          </div>
          <div className="text-[12px] text-fg-muted">
            {stage.type === 'uploading'
              ? 'Upload direto ao R2, sem passar pelo nosso servidor.'
              : 'Pode levar 5–10 segundos. Não feche esta tela.'}
          </div>
        </div>
      )}

      {stage.type === 'done' && (
        <div className="rounded-3 border border-card-stroke bg-card p-4 space-y-3">
          <div className="flex items-center gap-2.5">
            <CheckCircle2 className="h-5 w-5 text-mint-deep" strokeWidth={1.8} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-fg">Documento processado</div>
              <div className="text-[12.5px] text-fg-muted">
                {stage.result.ocrResult.emitenteNome ?? 'Emitente a identificar'}
                {stage.result.ocrResult.valorTotal != null && (
                  <> · R$ {stage.result.ocrResult.valorTotal.toFixed(2).replace('.', ',')}</>
                )}
                {' · '}
                {Math.round(stage.result.ocrResult.confianca * 100)}% confiança
              </div>
            </div>
            <Pill tone={stage.result.needsReview ? 'warn' : 'pass'}>
              {stage.result.needsReview ? 'Em revisão' : 'Aprovado'}
            </Pill>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-card-stroke">
            <Button type="button" variant="ghost" onClick={reset}>
              <RefreshCw className="h-4 w-4" strokeWidth={1.8} />
              Capturar outro
            </Button>
          </div>
        </div>
      )}

      {stage.type === 'error' && (
        <div className="rounded-3 border border-warn-stroke bg-warn-soft p-4 space-y-3" role="alert">
          <div className="text-sm font-medium text-warn">Não foi possível processar</div>
          <div className="text-[12.5px] text-fg-muted">{stage.message}</div>
          <Button type="button" variant="ghost" onClick={reset}>
            <RefreshCw className="h-4 w-4" strokeWidth={1.8} />
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  );
}
